// "Add to plan" on the recipe page: pick a meal and a day (or "Every day") in
// the week being viewed, and add or remove the recipe there. Snack-only,
// candy, preserves and medicinal recipes go on the week's lists instead, so
// they only get the button. The same logic the old recipe view had; the
// plan picker of slice 5 replaces it.
import { useState } from "react";
import { COLORS, DAYS, FONT_SIZE, MEAL_SLOTS, RADIUS, SPACE } from "../lib/constants";
import { capitalizeFirst, orderedDays } from "../lib/utils";
import type { Day, MealSlot, Recipe, WeekPlan } from "../lib/types";

type Assign = (recipeId: string, slot: string, dishType: string, day?: string) => unknown;

interface Props {
  recipe: Recipe & { mealTypes?: string[] };
  weekData: WeekPlan;
  weekStartDay: Day;
  weekLabel: string;
  onAssign: Assign;
  onRemove: Assign;
}

const selectStyle = { minHeight: 44, padding: "0 var(--space-md)", borderRadius: RADIUS.md, border: `1px solid ${COLORS.line}`, background: COLORS.card, color: COLORS.ink, fontSize: FONT_SIZE.base };

export function AddToPlanPanel({ recipe, weekData, weekStartDay, weekLabel, onAssign, onRemove }: Props) {
  const mealTypes = recipe.mealTypes || [];
  const isPreserves = recipe.recipeType === "preserves";
  const isMedicinal = recipe.recipeType === "medicinal";
  const isSnackOnly = recipe.recipeType === "candy" || (mealTypes.length > 0 && mealTypes.every((m) => m === "snack"));
  const isWeeklyList = isPreserves || isMedicinal || isSnackOnly;
  const eligible = mealTypes.filter((m): m is MealSlot => (MEAL_SLOTS as readonly string[]).includes(m));
  const [slot, setSlot] = useState<MealSlot>(eligible[0] || "dinner");
  const [day, setDay] = useState<Day | "every day">(orderedDays(weekStartDay)[0] as Day);

  const listName = isPreserves ? "preserves" : isMedicinal ? "medicinal" : "snacks";
  const pseudoSlot = isPreserves ? "preserves" : isMedicinal ? "medicinal" : isSnackOnly ? "snack" : slot;
  const inSlot = (d: Day) => (weekData.days[d]?.[slot] as Record<string, string> | undefined)?.[recipe.recipeType] === recipe.id;
  const isAssigned = isWeeklyList ? (weekData[listName] || []).some((e) => e.recipeId === recipe.id) : day === "every day" ? DAYS.every(inSlot) : inSlot(day);

  function toggle() {
    const target = isWeeklyList ? undefined : day;
    (isAssigned ? onRemove : onAssign)(recipe.id, pseudoSlot, recipe.recipeType, target);
  }

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.md, padding: "var(--space-xl) 0", borderBottom: `1px solid ${COLORS.line}` }}>
      <span style={{ fontSize: FONT_SIZE.sm, color: COLORS.soft }}>{isWeeklyList ? `On the ${listName === "snacks" ? "snack" : listName} list for ${weekLabel}` : `In ${weekLabel}`}</span>
      <div style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap", alignItems: "center" }}>
        {!isWeeklyList && (
          <>
            <select aria-label="Meal" value={slot} onChange={(e) => setSlot(e.target.value as MealSlot)} style={selectStyle}>
              {MEAL_SLOTS.map((m) => (
                <option key={m} value={m}>
                  {capitalizeFirst(m)}
                </option>
              ))}
            </select>
            <select aria-label="Day" value={day} onChange={(e) => setDay(e.target.value as Day | "every day")} style={selectStyle}>
              {(orderedDays(weekStartDay) as Day[]).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
              <option value="every day">Every day</option>
            </select>
          </>
        )}
        <button
          type="button"
          className="mp-btn"
          onClick={toggle}
          style={{ minHeight: 44, padding: "0 var(--space-x3)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base, fontWeight: 700, ...(isAssigned ? { background: "transparent", color: COLORS.danger, border: `1.5px solid ${COLORS.danger}` } : { background: COLORS.accent, color: COLORS.onAccent }) }}
        >
          {isAssigned ? "Remove from plan" : "Add to plan"}
        </button>
      </div>
    </div>
  );
}
